import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service, TemplateResolve } from 'webapp-types';
import { BillingFeature, RcItem } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { isAlive } from 'webapp/utils/common';

import BarLoaderOverlayComponent from 'webapp/components/bar-loader-overlay';
import CommonResolutioncenterItemSourceChoiceComponent from 'webapp/components/common/resolutioncenter-item-source-choice';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';
import type SourceRecord from 'webapp/models/source';

interface Args {
	model: ResolutionCenterItem;
	onClose: () => void;
}

export default class CommonResolutioncenterItemComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('popup') declare popupService: Service.Popup;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('request') declare requestService: Service.Request;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;

	@tracked isTextShortened: boolean;
	@tracked isRequestPending = false;
	@tracked isResendLinkDisplayed = false;
	@tracked isContactUsDisplayed = false;

	maxTextSigns = 100;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.isTextShortened = this.modelShownText.length > 0;
	}

	get model() {
		return this.args.model;
	}

	get modelShownText() {
		return this.model.shownText;
	}

	get shownText() {
		let text = this.modelShownText;
		if (this.isTextShortened) {
			text = text.substring(0, this.maxTextSigns) + '...';
		}
		return text;
	}

	get sourceToConnect() {
		const dataSourceTypeId = this.model.params.dataSourceTypeId;

		let source;
		if (this.model.type === RcItem.CONNECT_SOURCE && dataSourceTypeId) {
			source = this.mmSourceTypesService.findModelById(dataSourceTypeId);
		}
		return source;
	}

	get sourcesToChoose() {
		const { dataSourceTypeId } = this.model.params;

		let sources: SourceRecord[] = [];
		if (this.model.type === RcItem.CHOOSE_SOURCE && dataSourceTypeId) {
			sources = this.mmSourcesService.savedRecords.filter(
				(item) => item.type === dataSourceTypeId,
			);
		}
		return sources;
	}

	get displayBoardTransferOptions() {
		return this.model.isTransferedBoardType;
	}

	solveSourceConnect() {
		const sourceType = this.sourceToConnect,
			msgId = this.model.id;

		if (!msgId || !sourceType?.id) {
			return;
		}

		if (
			sourceType &&
			!this.mmSourcesService.savedRecords.find((item) => item.type === sourceType.id)
		) {
			void sourceType.connectNew({ origin: 'resolutioncenterItem' }).then((result) => {
				if (result.isSuccess) {
					const source = result.source;
					const data: TemplateResolve.NormalizedResolvePayload = {
						dataSourceTypeMap: {},
					};

					if (sourceType.id) {
						data.dataSourceTypeMap[sourceType.id] = String(source.id);
					}

					this.requestService
						.api('POST', '/resolution-center/' + msgId, data)
						.catch(
							this.requestService.handleGenericThrownError.bind(this.requestService),
						);
				}
			});
		} else {
			const board = this.mmBoardsService.normalBoardModels.find(
				(item) => item.id === String(this.model.boardIds[0]),
			);
			if (board) {
				this.mmBoardsService.openResolveDataPopup({ model: board });
			}
		}
	}

	click() {
		if (this.model.type === RcItem.CONNECT_SOURCE) {
			this.solveSourceConnect();
		}
	}

	@action
	resendVerifyEmail() {
		const wasTriggered = this.eventManagerService.trigger(AppEvent.RESEND_VERIFY);
		if (!wasTriggered) {
			void this.currentUserService.resendVerifyEmail();
		}

		this.model.title =
			'A confirmation link has been sent to ' +
			this.currentUserService.model?.email +
			". If you aren't getting the email, don't hesitate to get in touch with us.";

		this.isResendLinkDisplayed = true;
		this.isContactUsDisplayed = true;
	}

	@action
	showFullText() {
		this.isTextShortened = false;
	}

	@action
	choose(sourceId: string) {
		const data: TemplateResolve.NormalizedResolvePayload = { dataSourceTypeMap: {} },
			msgId = this.model.id;

		data.dataSourceTypeMap[this.model.params.dataSourceTypeId!] = sourceId; // set on source messages

		if (!msgId) {
			return;
		}

		this.isRequestPending = true;
		this.requestService
			.api('POST', '/resolution-center/' + msgId, data)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
			.finally(() => {
				this.isRequestPending = false;
			});
	}

	@action
	contactUs() {
		this.currentUserService.chatWithSupport();
	}

	@action
	handleTransferedBoard(isAccept: boolean) {
		const onAllResolved = (data?: TemplateResolve.NormalizedResolvePayload) => {
			const msgId = this.model.id!; // a message from BE

			if (isAlive(this)) {
				this.isRequestPending = true;
			}

			this.appNotificationsService
				.resolveTemplateNotificationFromResolutionCenter(isAccept, msgId, data)
				.then(() => {
					this.args.onClose();
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		};

		const boardsCount = this.model.params.boards?.length || 1;
		if (isAccept) {
			if (this.billingService.checkIfCan(BillingFeature.BOARDS, true, boardsCount)) {
				if (this.model.isTransferedBoardType) {
					this.mmTemplatesService.openResolvePopup(this.model, onAllResolved);
				}
			}
		} else {
			onAllResolved();
		}
	}
	<template>
		<li>
			<div>
				<span class="icons {{@model.prioclass}}">
					<CommonUiIconComponent @name="AlertFill" @size="sm" @class="error" />
					<CommonUiIconComponent @name="AlertFill" @size="sm" @class="warning" />
					<CommonUiIconComponent @name="Check" @size="sm" @class="check" />
					<CommonUiIconComponent @name="InfoFill" @size="sm" @class="info" />
				</span>
				<strong>{{@model.title}}</strong>
				{{#if @model.timeText}}
					<small>{{@model.timeText}}</small>
				{{/if}}
				<p>
					{{{this.shownText}}}
					{{#if this.isTextShortened}}
						<a role="button" class="showall" {{on "click" this.showFullText}}>Read more</a>
					{{/if}}
					{{#if this.isResendLinkDisplayed}}
						<a role="button" {{on "click" this.resendVerifyEmail}}>
							Resend verification email
						</a>
					{{/if}}
					{{#if this.isContactUsDisplayed}}
						<a role="button" {{on "click" this.contactUs}}>Contact us</a>
					{{/if}}
					{{!--
            {{#if isEmptySourceTypes}}
                <div class="source-actions">
                    {{#if displayQBSourceActions}}
                        {{#unless billing.qbEnabled}}{{#link-to route="settings.billing.plans"}}Upgrade to Plus{{/link-to}} | {{/unless}}
                        <a href="https://databox.com/resources/help/query-builder" target="_blank">More about Metric Builder</a>
                    {{else}}
                        {{#link-to route="designer"}}Create a dashboard{{/link-to}}
                    {{/if}}
                    <br>
                    <a role="button" {{action "deleteSource"}} class="delete-connection">Delete this connection</a>
                </div>
            {{/if}}
            --}}
					{{#if this.displayBoardTransferOptions}}
						<div class="source-actions">
							<a
								role="button"
								class="blue"
								{{on "click" (fn this.handleTransferedBoard true)}}
							>
								{{@model.acceptText}}
							</a>
							|
							<a
								role="button"
								class="red"
								{{on "click" (fn this.handleTransferedBoard false)}}
							>
								Delete
							</a>
						</div>
					{{/if}}
				</p>
				{{#if this.sourceToConnect}}
					<a role="button">Connect {{this.sourceToConnect.name}}</a>
				{{/if}}
				{{#if this.sourcesToChoose}}
					<ul class="linklist">
						{{#each this.sourcesToChoose as |source|}}
							<CommonResolutioncenterItemSourceChoiceComponent
								@model={{source}}
								@onChoose={{this.choose}}
							/>
						{{/each}}
					</ul>
				{{/if}}
			</div>

			<BarLoaderOverlayComponent @isActive={{this.isRequestPending}} />
		</li>
	</template>
}
